import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Add } from "iconsax-reactjs";
import { toast } from "sonner";
import { DataTable } from "../../../shared/ui/DataTable";
import { Button, HeadingText, BodyText, PaginationControls, Shimmer } from "../../../shared/ui";
import { RootState } from "../../../redux/store";
import { getApiErrorMessage } from "../../../shared/lib";
import { useNotifications } from "../hooks/useNotifications";
import { notificationColumns } from "./notificationColumns";
import NotificationComposeModal from "./NotificationComposeModal";
import NotificationDetailFlyin from "./NotificationDetailFlyin";
import { DEFAULT_AUDIENCE_FILTERS } from "../constants/notificationData";
import {
  getBufferedRequestLimit,
  getBufferedRequestPage,
  getUiTotalPages,
} from "../../../shared/lib/bufferedPagination";
import {
  AudienceFilters,
  BroadcastNotification,
  ComposeBroadcastPayload,
  NotificationChannel,
} from "../types";

const NotificationSection = () => {
  const pageSize = useSelector((state: RootState) => state.totalItemPerpage) || 20;
  const [currentPage, setCurrentPage] = useState(1);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [channel, setChannel] = useState<NotificationChannel>("email");
  const [audience, setAudience] = useState<AudienceFilters>(DEFAULT_AUDIENCE_FILTERS);
  const [viewingItem, setViewingItem] = useState<BroadcastNotification | null>(null);

  const requestLimit = getBufferedRequestLimit(pageSize);
  const requestPage = getBufferedRequestPage(currentPage);

  const {
    items: notifications,
    meta,
    isLoading,
    error,
    isSending,
    sendBroadcast,
    saveDraft,
    updateDraft,
    deleteDraft,
    refetch,
  } = useNotifications({ page: requestPage, limit: requestLimit });

  const totalPages = getUiTotalPages(meta, pageSize, requestLimit, notifications.length);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const resetComposeForm = () => {
    setSubject("");
    setMessage("");
    setChannel("email");
    setAudience(DEFAULT_AUDIENCE_FILTERS);
    setEditingDraftId(null);
  };

  const handleOpenChange = (open: boolean) => {
    setIsComposeOpen(open);
    if (!open) resetComposeForm();
  };

  const handleNewBroadcast = () => {
    resetComposeForm();
    setIsComposeOpen(true);
  };

  const handleRowClick = (item: BroadcastNotification) => {
    setViewingItem(item);
  };

  const openComposeFrom = (item: BroadcastNotification, { asDraft }: { asDraft: boolean }) => {
    setSubject(item.subject);
    setMessage(item.message);
    setChannel(item.channel);
    setAudience(item.audience);
    setEditingDraftId(asDraft ? item.id : null);
    setViewingItem(null);
    setIsComposeOpen(true);
  };

  const handleEditDraft = (item: BroadcastNotification) => {
    openComposeFrom(item, { asDraft: true });
  };

  const handleReuseTemplate = (item: BroadcastNotification) => {
    openComposeFrom(item, { asDraft: false });
  };

  const handleSend = async (payload: ComposeBroadcastPayload) => {
    try {
      if (editingDraftId) {
        await updateDraft(editingDraftId, payload, true);
      } else {
        await sendBroadcast(payload);
      }
      toast.success("Broadcast sent");
      setIsComposeOpen(false);
      resetComposeForm();
      setCurrentPage(1);
      refetch();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Unable to send broadcast"));
    }
  };

  const handleSaveDraft = async (payload: ComposeBroadcastPayload) => {
    try {
      if (editingDraftId) {
        await updateDraft(editingDraftId, payload, false);
      } else {
        await saveDraft(payload);
      }
      toast.success("Draft saved");
      setIsComposeOpen(false);
      resetComposeForm();
      refetch();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Unable to save draft"));
    }
  };

  const performDeleteDraft = async (id: string) => {
    try {
      await deleteDraft(id);
      toast.success("Draft deleted");
      refetch();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Unable to delete draft"));
    }
  };

  const handleDeleteDraft = async () => {
    if (!editingDraftId) return;
    await performDeleteDraft(editingDraftId);
    setIsComposeOpen(false);
    resetComposeForm();
  };

  const handleDeleteDraftFromDetail = async (item: BroadcastNotification) => {
    setViewingItem(null);
    await performDeleteDraft(item.id);
  };

  return (
    <section className="w-full">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <HeadingText className="text-[28px] font-[450] text-[#161827]">
            Broadcast
          </HeadingText>
          <BodyText className="mt-1 text-[#6F739B]">
            Send broadcast messages to customers and review what's already gone out.
          </BodyText>
        </div>
        <Button
          onClick={handleNewBroadcast}
          className="self-start px-5 text-[14px] font-[550] sm:self-auto"
        >
          <Add size={18} />
          New broadcast
        </Button>
      </div>

      <div className="mt-6">
        {isLoading ? (
          <DataTable
            columns={notificationColumns}
            data={[]}
            getRowKey={(item) => item.id}
            emptyState={
              <div className="space-y-3 py-2">
                {Array.from({ length: 5 }).map((_, index) => (
                  <Shimmer key={index} className="h-8 w-full rounded-[10px]" />
                ))}
              </div>
            }
          />
        ) : error ? (
          <DataTable
            columns={notificationColumns}
            data={[]}
            getRowKey={(item) => item.id}
            emptyState={
              <div className="space-y-3 py-2">
                <div className="text-[14px] text-[#C24E4E]">{error}</div>
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 rounded-[12px]"
                  onClick={refetch}
                >
                  Retry
                </Button>
              </div>
            }
          />
        ) : (
          <DataTable
            columns={notificationColumns}
            data={notifications}
            getRowKey={(item) => item.id}
            onRowClick={handleRowClick}
            emptyState={
              <div className="py-2 text-[#6F739B]">
                No broadcasts sent yet. Click "New broadcast" to send the first one.
              </div>
            }
          />
        )}

        {!isLoading && !error && notifications.length > 0 ? (
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            className="mt-8 flex items-center gap-2"
          />
        ) : null}
      </div>

      <NotificationComposeModal
        open={isComposeOpen}
        onOpenChange={handleOpenChange}
        subject={subject}
        onSubjectChange={setSubject}
        message={message}
        onMessageChange={setMessage}
        channel={channel}
        onChannelChange={setChannel}
        audience={audience}
        onAudienceChange={setAudience}
        isSending={isSending}
        isEditingDraft={Boolean(editingDraftId)}
        onSend={handleSend}
        onSaveDraft={handleSaveDraft}
        onDeleteDraft={editingDraftId ? handleDeleteDraft : undefined}
      />

      <NotificationDetailFlyin
        open={Boolean(viewingItem)}
        item={viewingItem}
        onOpenChange={(open) => {
          if (!open) setViewingItem(null);
        }}
        onEditDraft={handleEditDraft}
        onDeleteDraft={handleDeleteDraftFromDetail}
        onReuseTemplate={handleReuseTemplate}
      />
    </section>
  );
};

export default NotificationSection;
